import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import VideoBackground from '../../../components/base/VideoBackground';
import FloatingSocialIcons from '../../../components/base/FloatingSocialIcons';

const SecurityPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-50 relative overflow-hidden">
      <VideoBackground />
      <FloatingSocialIcons />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-slate-600 via-gray-600 to-blue-600 bg-clip-text text-transparent mb-6">
            Website Security & Maintenance
          </h1>
          <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
            Protect your website and customer data with comprehensive security solutions and proactive maintenance services.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-slate-600 to-gray-600 bg-clip-text text-transparent">
            Security Benefits for Small Businesses
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-slate-500 to-gray-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Protect Customer Data</h3>
              <p className="text-gray-600">Safeguard sensitive customer information and maintain trust with robust security measures.</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-gray-500 to-blue-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-time-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Minimize Downtime</h3>
              <p className="text-gray-600">Prevent costly website outages and ensure your business stays online 24/7.</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-award-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Build Credibility</h3>
              <p className="text-gray-600">Show customers you take security seriously with SSL certificates and security badges.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-gray-600 to-blue-600 bg-clip-text text-transparent">
            Our Security Services Include
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-slate-100 to-gray-100 p-6 rounded-xl">
              <i className="ri-shield-line text-3xl text-slate-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">SSL Certificates</h4>
              <p className="text-sm text-gray-600">Secure data transmission with HTTPS encryption</p>
            </div>
            <div className="bg-gradient-to-br from-gray-100 to-blue-100 p-6 rounded-xl">
              <i className="ri-virus-line text-3xl text-gray-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Malware Protection</h4>
              <p className="text-sm text-gray-600">Regular scanning and removal of malicious code</p>
            </div>
            <div className="bg-gradient-to-br from-blue-100 to-teal-100 p-6 rounded-xl">
              <i className="ri-database-2-line text-3xl text-blue-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Regular Backups</h4>
              <p className="text-sm text-gray-600">Automated daily backups with quick restore options</p>
            </div>
            <div className="bg-gradient-to-br from-teal-100 to-green-100 p-6 rounded-xl">
              <i className="ri-refresh-line text-3xl text-teal-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Software Updates</h4>
              <p className="text-sm text-gray-600">Keep all plugins and themes updated for security</p>
            </div>
            <div className="bg-gradient-to-br from-green-100 to-yellow-100 p-6 rounded-xl">
              <i className="ri-eye-line text-3xl text-green-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Security Monitoring</h4>
              <p className="text-sm text-gray-600">24/7 monitoring for security threats and attacks</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-100 to-orange-100 p-6 rounded-xl">
              <i className="ri-tools-line text-3xl text-yellow-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Performance Optimization</h4>
              <p className="text-sm text-gray-600">Regular maintenance for optimal website speed</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Links */}
      <section className="py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-xl font-bold mb-6 text-gray-800">Stay Updated on Security Best Practices</h3>
          <div className="flex justify-center space-x-6">
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-slate-500 to-gray-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-facebook-fill text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-gray-500 to-blue-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-instagram-line text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-twitter-line text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-teal-500 to-green-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-linkedin-fill text-xl"></i>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-slate-500 via-gray-500 to-blue-500 rounded-3xl p-8 text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Secure Your Website?</h2>
          <p className="text-xl mb-8">Let's protect your business with comprehensive security and maintenance solutions!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/consultation"
              className="bg-white text-gray-600 px-8 py-4 rounded-full font-bold hover:bg-gray-100 transition-colors"
            >
              Book Free Consultation
            </Link>
            <Link 
              to="/quote"
              className="border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white hover:text-gray-600 transition-colors"
            >
              Request Free Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SecurityPage;